'use client';

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { AttachmentFileLink } from '@/components/common/AttachmentFileLink';
import { PhotoLightbox } from '@/components/common/PhotoLightbox';
import {
  collectAttachmentMedia,
  useWorksheetPhotos,
  VISIBLE_PHOTO_COUNT,
  WorksheetPhotoContext,
  type WorksheetPhoto,
} from '@/components/common/worksheetPhoto';
import { WorksheetPhotoThumb } from '@/components/common/WorksheetPhotoThumb';

export function WorksheetPhotoProvider({
  outputs,
  children,
}: {
  outputs: { output: unknown }[] | undefined;
  children: ReactNode;
}) {
  const media = useMemo(() => collectAttachmentMedia(outputs), [outputs]);
  const photosRef = useRef(media.photos);
  photosRef.current = media.photos;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [single, setSingle] = useState<WorksheetPhoto[] | null>(null);

  const registeredRef = useRef<WorksheetPhoto[]>([]);
  const registerPhoto = useCallback((photo: WorksheetPhoto) => {
    const list = registeredRef.current;
    if (list.length >= 24) return;
    if (list.some(item => item.url === photo.url)) return;
    list.push(photo);
  }, []);

  const openPhoto = useCallback((photo: WorksheetPhoto) => {
    const toolGallery = photosRef.current;
    const toolIndex = toolGallery.findIndex(item => item.url === photo.url);
    if (toolIndex >= 0) {
      setSingle(null);
      setLightboxIndex(toolIndex);
      return;
    }
    const registered = registeredRef.current;
    const index = registered.findIndex(item => item.url === photo.url);
    if (index >= 0) {
      setSingle(registered.slice());
      setLightboxIndex(index);
      return;
    }
    setSingle([photo]);
    setLightboxIndex(0);
  }, []);

  const openAt = useCallback((index: number) => {
    setSingle(null);
    setLightboxIndex(index);
  }, []);

  const close = useCallback(() => {
    setLightboxIndex(null);
    setSingle(null);
  }, []);

  const value = useMemo(
    () => ({
      photos: media.photos,
      files: media.files,
      openPhoto,
      openAt,
      registerPhoto,
    }),
    [media.photos, media.files, openPhoto, openAt, registerPhoto]
  );
  const gallery = single ?? media.photos;

  return (
    <WorksheetPhotoContext.Provider value={value}>
      {children}
      {lightboxIndex !== null && gallery[lightboxIndex] ? (
        <PhotoLightbox
          photos={gallery}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={close}
        />
      ) : null}
    </WorksheetPhotoContext.Provider>
  );
}

export function WorksheetPhotoStrip() {
  const gallery = useWorksheetPhotos();
  if (!gallery || (gallery.photos.length === 0 && gallery.files.length === 0)) {
    return null;
  }

  const visible = gallery.photos.slice(0, VISIBLE_PHOTO_COUNT);
  const hidden = gallery.photos.length - visible.length;

  return (
    <div className='min-w-0 max-w-full px-2' data-cy='worksheet-photo-strip'>
      {gallery.photos.length ? (
        <div
          className='flex w-full max-w-full gap-2 overflow-x-auto pb-1'
          role='group'
          aria-label='Photos from this reply'
        >
          {visible.map((photo, index) => (
            <WorksheetPhotoThumb
              key={photo.url}
              photo={photo}
              onOpen={() => gallery.openAt(index)}
            />
          ))}
          {hidden > 0 ? (
            <button
              type='button'
              data-cy='worksheet-photo-more'
              aria-label={`Show ${hidden} more photos`}
              onClick={() => gallery.openAt(VISIBLE_PHOTO_COUNT)}
              className='inline-flex h-[72px] w-16 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-sm tabular-nums text-fg-default ring-1 ring-inset ring-white/10 transition-transform duration-150 ease-[cubic-bezier(0.2,0,0,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100'
            >
              +{hidden}
            </button>
          ) : null}
        </div>
      ) : null}
      {gallery.files.length ? (
        <div
          className={
            gallery.photos.length
              ? 'mt-2 flex max-w-full flex-wrap gap-2'
              : 'flex max-w-full flex-wrap gap-2'
          }
          role='group'
          aria-label='Files from this reply'
        >
          {gallery.files.map(file => (
            <AttachmentFileLink key={file.url} file={file} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
